import cn from "classnames";
import { forwardRef } from "react";
import { A11y } from "../a11y";
import { ButtonProps } from "./types";
import { BUTTON_TYPES_WITH_LOADER } from "./constants";
import styles from "./styles.module.scss";

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      id,
      type = "primary",
      size = "medium",
      disabled = false,
      isLoading = false,
      dataTestId = "button",
      htmlType = "submit",
      children,
      className,
      hasDisabledA11yFocus,
      onBlur,
      onFocus,
      onClick,
      onMouseEnter,
      onMouseLeave,
      ...otherAttributes
    },
    forwardedRef
  ) => {
    const handleMouseEnter = (event: React.MouseEvent<HTMLButtonElement>) => {
      onMouseEnter?.(event);
    };

    const handleFocus = (event: React.FocusEvent<HTMLButtonElement>) => {
      onFocus?.(event);
    };

    const handleBlur = (event: React.FocusEvent<HTMLButtonElement>) => {
      onBlur?.(event);
    };

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      onClick?.(event);
    };

    const handleMouseLeave = (event: React.MouseEvent<HTMLButtonElement>) => {
      onMouseLeave?.(event);
    };

    const isLoaderVisible =
      isLoading && BUTTON_TYPES_WITH_LOADER.includes(type);

    const buttonClassnames = cn(
      styles.button,
      styles[type],
      {
        [styles[size]]: type !== "circle" && type !== "icon",
      },
      className
    );

    const content = isLoaderVisible ? (
      <>
        {children}
        <span
          className={styles["loader-wrapper"]}
          data-test-id={`${dataTestId}-loader-wrapper`}
        >
          <span
            className={styles.loader}
            data-test-id={`${dataTestId}-loader`}
          />
        </span>
      </>
    ) : (
      children
    );

    return (
      <A11y hasDisabledA11yFocus={hasDisabledA11yFocus} ref={forwardedRef}>
        <button
          {...otherAttributes}
          id={id}
          type={htmlType}
          disabled={disabled || isLoaderVisible}
          data-test-id={dataTestId}
          className={buttonClassnames}
          onBlur={handleBlur}
          onClick={handleClick}
          onFocus={handleFocus}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {content}
        </button>
      </A11y>
    );
  }
);
